import { apiService } from '@/services/api/apiService';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Key, ReactNode } from 'react';

interface CartItem {
  weeklyScheduleId: number;
  addedAt: string;
  isActive: boolean;
  quantity: number;
  lineTotal: number;
  menuTitle: string;
  unitPrice: number;
  cartItemId: number;
  foodMenuId: number;
  mealTypeId: number;
  isCustomized: boolean;
  scheduledDate: string;
  foodMenuItemId: number;
  foodScheduleId: number;
  menuDescription: string | null;
  customizationNotes: string | null;
}

interface Cart {
  cookProfileIds: never[];
  items: any;
  areaCodeId: any;
  cartId: number;
  createdAt: string;
  cartReference: string;
  lastUpdatedAt: string;
}

interface CartData {
  areaCodeIds: never[];
  cookProfileIds: never[];
  foodMenuIds: never[];
  foodScheduleIds: never[];
  cartId: number;
  quantity: any;
  cart: Cart;
  page: { limit: number; offset: number };
  items: CartItem[];
  status: string;
  subtotal: number;
  itemCount: number;
}

export interface CustomerCartResponse {
  quantity: number;
  cartId: any;
  data: CartData;
  isSuccess: boolean;
  status: number;
}

interface CustomerCartState {
  data: CustomerCartResponse | null;
  loading: boolean;
  error: string | null;
}

const PORT = import.meta.env.VITE_FOOD_ORDER_PORT;
const URL = import.meta.env.VITE_API_FOOD_ORDER_URL;

export const fetchCustomerCart = createAsyncThunk<
  CustomerCartResponse,
  { userProfileId: number | string; areaCodeId?: number | string; isForCustomer?: boolean },
  { rejectValue: string }
>('CustomerCart/fetchCustomerCart', async (params, { rejectWithValue }) => {
  try {
    const config = { params };

    // Expect full object, not just an array
    const response = await apiService.get<CustomerCartResponse>(
      'get_all_customer_cart',
      PORT,
      URL,
      config,
    );

    return response; // return full CustomerCartResponse
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred');
  }
});

export const createCustomerCart = createAsyncThunk<
  CustomerCartResponse,
  any,
  { rejectValue: string }
>('CustomerCart/createCustomerCart', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.post<{ data: CustomerCartResponse }>(
      'create_customer_cart',
      params,
      PORT,
      URL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const updateCustomerCart = createAsyncThunk<
  CustomerCartResponse,
  any,
  { rejectValue: string }
>('CustomerCart/updateCustomerCart', async (params, { rejectWithValue }) => {
  try {
    const response = await apiService.put<{ data: CustomerCartResponse }>(
      'update_customer_cart',
      params,
      PORT,
      URL,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const customerCartSlice = createSlice({
  name: 'CustomerCart',
  initialState: {
    data: null,
    loading: false,
    error: null,
  } as CustomerCartState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCustomerCart.fulfilled,
        (state, action: PayloadAction<CustomerCartResponse>) => {
          state.loading = false;
          state.data = action.payload;
        },
      )
      .addCase(fetchCustomerCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

export default customerCartSlice.reducer;
